/**
 * The page a document is written on.
 *
 * A DOCUMENT IS A PAGE, AND THE SHAPE IS NOT DECORATION. A controlled procedure is written to be
 * printed, signed and filed: the author needs to see where the line breaks, how much is on a page
 * and whether a table fits across the width. A full-browser-width text box tells them none of that,
 * and what looked fine on a 27-inch screen arrives at the printer with a table running off the edge.
 *
 * US LETTER AT 1-INCH MARGINS, in real inches rather than pixels, because that is what the rendition
 * uses and the editor should not be a different document from the one that gets signed. Browsers
 * resolve `in` against 96 CSS pixels, so the page is 816px wide and the text column 624px -- the
 * same measure the PDF will have.
 *
 * SCROLLED, NOT PAGINATED. Showing where page two begins means laying out the text the way the
 * renderer will, which is the renderer's job and a different engine from the editor's. A false page
 * break drawn at a guessed height would be worse than none: it would be wrong at exactly the moment
 * somebody relied on it.
 *
 * THE GREY CANVAS IS WHAT MAKES THE PAGE A PAGE. White on white is a box; white on grey is paper.
 */
import React from "react";
import { Box } from "@mui/material";

import { EditorRulerVertical } from "./EditorRuler";
import { PAGE_HEIGHT, PAGE_MARGIN } from "./pagination";
import { pageGeometry } from "./PageElements";

const PAGE_MARGIN_IN = `${PAGE_MARGIN}px`;

/**
 * The watermark, as a background layer tiled once per sheet.
 *
 * PAINTED, NOT STACKED, for the same reason the page edges are: there is no per-page element to
 * hang one on, because the text lives in ONE contenteditable and dividing it would be a different
 * editor. An SVG sized to a sheet and repeated at the sheet pitch puts the mark in the middle of
 * every page for free, including pages that do not exist yet.
 *
 * BEHIND THE TEXT BY CONSTRUCTION. It is a background of the page element and the text is that
 * element's content, so nothing can be hidden by it and no z-index has to be maintained.
 *
 * THE SIZE IS DERIVED FROM THE WORD. "DRAFT" and "PENDING CHANGE" are very different widths, and a
 * fixed size either shrinks the short one to nothing or runs the long one off the diagonal.
 */
function watermarkLayer(text: string, width: number, height: number): string {
    // Across the diagonal, which is the longest line on the sheet and what a stamp follows.
    const size = Math.min(150, Math.max(48, (width * 1.25) / text.length));
    const svg = [
        `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">`,
        // 10%, MEASURED RATHER THAN CHOSEN. At 6% the mark was present in the DOM and invisible on
        // the screen, which is the worst of both: it satisfies a test and fails a reader. A
        // watermark has to be legible at a glance and must not compete with the text it sits under.
        `<text x="50%" y="50%" fill="#000000" fill-opacity="0.10"`,
        ` font-family="Arial, Helvetica, sans-serif" font-size="${size}" font-weight="700"`,
        ` letter-spacing="${Math.round(size / 12)}"`,
        ` text-anchor="middle" dominant-baseline="middle"`,
        ` transform="rotate(-35 ${width / 2} ${height / 2})">`,
        text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"),
        `</text></svg>`,
    ].join("");
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

export default function DocumentPaper({ editable, fullWidth, showRuler, zoom, footer,
                                       headerMargin, footerMargin, watermark, geometry,
                                       styleVariables, children }: {
    editable: boolean;
    /** View > Full width: the page gives up its margins and fills the window. */
    fullWidth?: boolean;
    /** The vertical ruler beside the page. Off at full width, where inches are not inches. */
    showRuler?: boolean;
    /** View zoom, as a percentage. A view setting: nothing in the document changes. */
    zoom?: number;
    /** The document's footer, drawn by the page rather than stored in the body. */
    footer?: React.ReactNode;
    /** Page setup, in inches from the top and bottom edges of the sheet. */
    headerMargin?: number;
    footerMargin?: number;
    /**
     * The mark across every sheet, or nothing. DERIVED FROM THE REVISION'S STATE by watermarkFor --
     * not typed, not a setting, and not something a draft can be printed without.
     */
    watermark?: string | null;
    /**
     * The sheet and its margins, from the document. Everything about the page that is not the text
     * comes from here, so the paper, the ruler, the pagination preview and the PDF agree.
     */
    geometry?: ReturnType<typeof pageGeometry>;
    /**
     * The document's named paragraph styles, as CSS custom properties.
     *
     * HUNG ON THE SHEET, not injected as a stylesheet: every rule below already reads its numbers
     * from these, with the old hardcoded value as its fallback, so a document carrying none renders
     * exactly as it did before styles existed. See namedStyles.ts.
     */
    styleVariables?: Record<string, string>;
    children: React.ReactNode;
}) {
    // DEFAULTED HERE RATHER THAN AT EVERY READ. A paper with no geometry is a paper laid out the
    // way every document was before page setup existed, which is what an unopened document is.
    const sheet = geometry ?? pageGeometry(null);

    return (
        <Box sx={{
            backgroundColor: "#F1F3F4", borderRadius: 1, py: 4,
            // RELATIVE, so the vertical ruler can pin itself to this box's left edge.
            position: "relative",
            // ALIGNED TO THE TOP, not stretched: the ruler runs the height of the PAGE, and a
            // centred row would leave it floating beside a short document.
            display: "flex", justifyContent: "center", alignItems: "flex-start",
            // The canvas scrolls with the window rather than inside itself: a document is read by
            // scrolling the page, and an inner scrollbar puts two of them on screen at once.
            overflowX: "auto",
        }}>
            {showRuler && <EditorRulerVertical geometry={sheet}/>}
            {/* THE CLASS IS WHAT PRINTING KEYS ON. See the @media print block in index.css: the
                chrome is hidden wholesale and this one element is shown, so nothing has to be
                enumerated and a control added to the toolbar next month cannot appear in a
                printout. */}
            <Box className="parkour-page" style={styleVariables as React.CSSProperties} sx={{
                // FULL WIDTH IS STILL A PAGE, just a wider one -- it keeps its margins and its
                // shadow. Dropping the paper entirely would be a different editor, and the point
                // of the option is reading comfort on a wide screen, not a change of medium.
                width: fullWidth ? "100%" : `${sheet.widthIn}in`,
                maxWidth: fullWidth ? "100%" : undefined,
                minHeight: `${sheet.pageHeight}px`, boxSizing: "border-box",
                // `zoom` RATHER THAN transform: scale. A transform leaves the element's layout box
                // at its old size, so the grey canvas keeps the height of an unzoomed page and a
                // 150% document runs off the bottom of its own background. zoom reflows.
                ...(zoom && zoom !== 100 ? { zoom: `${zoom}%` } : {}),
                // 1in all round, and the top one slightly tighter so the first line sits where the
                // eye expects rather than a full inch below a page edge that is already visible.
                // 1in ALL ROUND, in agreement with @page and with the pagination preview, which
                // counts a sheet as 11in with an inch of margin at each end. The top margin used to
                // be 0.9in to seat the first line a little higher; a tenth of an inch of prettiness
                // is not worth the editor and the printer disagreeing about where page two starts.
                // THE DOCUMENT'S MARGINS, four of them. They were one inch all round and hard
                // coded; the ruler numbers the column they leave and every stored indent is
                // measured into it, so this is the number that must not be guessed anywhere else.
                padding: `${sheet.marginTop}in ${sheet.marginRight}in `
                         + `${sheet.marginBottom}in ${sheet.marginLeft}in`,
                // THE RUNNING ELEMENTS SIT IN THE MARGIN, which is what a header margin means --
                // "half an inch from the top of the sheet", not "half an inch of text column".
                // The page keeps its inch of body margin and the header is pulled up into it by
                // the difference, exactly as a word processor places it. Format ▸ Headers & footers
                // ▸ More options.
                "--parkour-header-margin": `${headerMargin ?? 0.5}in`,
                "--parkour-footer-margin": `${footerMargin ?? 0.5}in`,
                // THE SHEETS ARE PAINTED, NOT STACKED. One element with a repeating gradient gives
                // every page edge for free -- white for 11in, then a band of canvas grey -- and
                // keeps the text in ONE contenteditable, which is the thing that must not be
                // divided: a selection, a paste or an undo that crossed a real element boundary
                // would be a different editor.
                // THE MARK IS A LAYER ON TOP OF THE SHEETS, listed first because CSS paints
                // background layers front to back. At full width there are no painted sheets to
                // tile against, so it is drawn once over the whole column rather than not at all --
                // a reading mode is still a view of a draft.
                backgroundRepeat: watermark ? "repeat-y" : undefined,
                backgroundSize: watermark
                    ? (fullWidth ? "100% 100%" : `100% ${sheet.sheetPitch}px`) : undefined,
                backgroundPosition: watermark ? "center top" : undefined,
                backgroundColor: fullWidth ? "#FFFFFF" : undefined,
                backgroundImage: fullWidth
                    ? (watermark
                        ? watermarkLayer(watermark, sheet.widthIn * 96, sheet.pageHeight) : undefined)
                    : [
                    "repeating-linear-gradient(to bottom,",
                    `#FFFFFF 0px, #FFFFFF ${sheet.pageHeight}px,`,
                    `rgba(0,0,0,.10) ${sheet.pageHeight}px, rgba(0,0,0,.10) ${sheet.pageHeight + 1}px,`,
                    `#F1F3F4 ${sheet.pageHeight + 1}px, #F1F3F4 ${sheet.sheetPitch - 1}px,`,
                    `rgba(0,0,0,.10) ${sheet.sheetPitch - 1}px, rgba(0,0,0,.10) ${sheet.sheetPitch}px)`,
                ].join(" ").replace(/^/, watermark
                    ? `${watermarkLayer(watermark, sheet.widthIn * 96, sheet.pageHeight)}, ` : ""),
                boxShadow: "0 1px 3px rgba(60,64,67,.3), 0 4px 8px 3px rgba(60,64,67,.15)",
                flexShrink: 0,

                // THE PAGE'S OWN TYPOGRAPHY, which is a document's and not the app's. 11pt in a
                // serif-free face at 1.5 is the house style of nearly every procedure written in
                // the last twenty years, and it is what the font and size controls start from.
                "& .ProseMirror": {
                    outline: "none", minHeight: "9in",
                    // THE COLUMNS, DRAWN BY THE BROWSER. CSS multi-column flows the text down one
                    // and on into the next, which is the same layout ReportLab reaches by giving
                    // the page a frame per column -- the measure comes from one derivation
                    // (pageGeometry) so the two cannot disagree about where a line wraps.
                    //
                    // NOTHING IS WRITTEN INTO THE DOCUMENT by this. The flow is a property of how
                    // the text is laid out, not of the text, so a column break is never a node and
                    // never reaches the content digest.
                    ...(sheet.columns > 1 ? {
                        columnCount: sheet.columns,
                        columnGap: `${sheet.columnGapIn}in`,
                        // BALANCED, NOT FILLED. `column-fill: auto` pours into the first column
                        // until it is full and only then starts the second -- which needs the
                        // container to HAVE a height, and this one grows with the document. Set
                        // that way the first column takes the whole procedure and the second is
                        // blank, which is what the first attempt here did. Balancing divides the
                        // text between the columns, which is what a reader expects to see when
                        // there are no page breaks to fill a column against.
                        columnFill: "balance",
                        // A HEADING SPANNING THE COLUMNS is a second thing to decide and to render,
                        // deliberately not done: a heading sits in its column like any other block,
                        // so the two ends agree without a rule about which.
                    } : {}),
                    // THE TEXT COLUMN IS THE ORIGIN the pagination measures against, so it has to
                    // be the offsetParent of the blocks inside it.
                    position: "relative",
                    fontFamily: "Arial, Helvetica, sans-serif",
                    // NORMAL TEXT IS A NAMED STYLE LIKE THE HEADINGS, and it is set HERE rather
                    // than on `p` so that everything which inherits body type -- list items, table
                    // cells, blockquotes -- follows it. Setting it on the paragraph alone would
                    // restyle the prose and leave the steps of the procedure behind.
                    fontSize: "var(--parkour-normal-size, 11pt)",
                    fontWeight: "var(--parkour-normal-weight, 400)" as any,
                    fontStyle: "var(--parkour-normal-style, normal)",
                    lineHeight: "var(--parkour-normal-leading, 1.5)" as any,
                    color: "var(--parkour-normal-colour, #202124)",
                },
                // EVERY NUMBER BELOW COMES FROM A CUSTOM PROPERTY, and the fallback in each one is
                // the value this file used to hardcode -- so a document that has never had a style
                // touched renders exactly as it did before named styles existed. The properties are
                // hung on the paper by DocumentEditor from the document's own `paragraphStyles`.
                //
                // VARIABLES RATHER THAN A SECOND STYLESHEET. These rules are written by MUI, which
                // generates class names and a specificity an injected sheet cannot reliably beat; a
                // custom property has no specificity at all, so the rule stays where it is and
                // simply reads its number from the document. See namedStyles.ts.
                "& .ProseMirror p": {
                    marginTop: "var(--parkour-normal-before, 0)",
                    marginBottom: "var(--parkour-normal-after, 10pt)",
                    marginLeft: 0, marginRight: 0,
                },
                // A SECTION HEAD IN A PROCEDURE IS SMALL, BOLD, AND RULED OFF -- not a magazine
                // headline. Every controlled document in the last thirty years draws it that way,
                // and the source of the first real document pasted in here proves it: DESCRIPTION,
                // CAUTION, MODE OF ACTION, each a line of bold caps with a rule beneath.
                //
                // THE RULE IS DRAWN HERE RATHER THAN CARRIED IN THE BODY, which is the whole point
                // of the heading being a heading. The source drew it with a one-cell table; the
                // paste transform throws that away and this puts the appearance back from the
                // stylesheet -- so the document LOOKS as it did and is now something a renderer can
                // build a contents page from.
                //
                // Sized in points, like the body, because this is the document's typography and not
                // the application's.
                "& .ProseMirror h1": {
                    fontSize: "var(--parkour-h1-size, 11pt)",
                    fontWeight: "var(--parkour-h1-weight, 700)" as any,
                    fontStyle: "var(--parkour-h1-style, normal)",
                    color: "var(--parkour-h1-colour, inherit)",
                    lineHeight: "var(--parkour-h1-leading, 1.5)" as any,
                    textTransform: "var(--parkour-h1-caps, none)" as any,
                    marginTop: "var(--parkour-h1-before, 16pt)",
                    marginBottom: "var(--parkour-h1-after, 6pt)",
                    borderBottom: "var(--parkour-h1-rule, 1px solid #000000)",
                    paddingBottom: "var(--parkour-h1-rule-pad, 2pt)",
                },
                "& .ProseMirror h2": {
                    fontSize: "var(--parkour-h2-size, 10.5pt)",
                    fontWeight: "var(--parkour-h2-weight, 700)" as any,
                    fontStyle: "var(--parkour-h2-style, normal)",
                    color: "var(--parkour-h2-colour, inherit)",
                    lineHeight: "var(--parkour-h2-leading, 1.5)" as any,
                    textTransform: "var(--parkour-h2-caps, none)" as any,
                    marginTop: "var(--parkour-h2-before, 14pt)",
                    marginBottom: "var(--parkour-h2-after, 5pt)",
                    borderBottom: "var(--parkour-h2-rule, none)",
                    paddingBottom: "var(--parkour-h2-rule-pad, 0)",
                },
                "& .ProseMirror h3": {
                    fontSize: "var(--parkour-h3-size, 10pt)",
                    fontWeight: "var(--parkour-h3-weight, 700)" as any,
                    fontStyle: "var(--parkour-h3-style, italic)",
                    color: "var(--parkour-h3-colour, inherit)",
                    lineHeight: "var(--parkour-h3-leading, 1.5)" as any,
                    textTransform: "var(--parkour-h3-caps, none)" as any,
                    marginTop: "var(--parkour-h3-before, 12pt)",
                    marginBottom: "var(--parkour-h3-after, 4pt)",
                    borderBottom: "var(--parkour-h3-rule, none)",
                    paddingBottom: "var(--parkour-h3-rule-pad, 0)",
                },
                // FOUR, FIVE AND SIX EXIST because Format ▸ Paragraph styles offers them, and a
                // style that applies no style is a control that lies. They step down in weight
                // rather than size: by the fourth level of a procedure the distinction being drawn
                // is small, and four sizes of bold in one document is noise.
                "& .ProseMirror h4": {
                    fontSize: "var(--parkour-h4-size, 10pt)",
                    fontWeight: "var(--parkour-h4-weight, 700)" as any,
                    fontStyle: "var(--parkour-h4-style, normal)",
                    color: "var(--parkour-h4-colour, inherit)",
                    lineHeight: "var(--parkour-h4-leading, 1.5)" as any,
                    textTransform: "var(--parkour-h4-caps, none)" as any,
                    marginTop: "var(--parkour-h4-before, 10pt)",
                    marginBottom: "var(--parkour-h4-after, 3pt)",
                    borderBottom: "var(--parkour-h4-rule, none)",
                    paddingBottom: "var(--parkour-h4-rule-pad, 0)",
                },
                "& .ProseMirror h5": {
                    fontSize: "var(--parkour-h5-size, 10pt)",
                    fontWeight: "var(--parkour-h5-weight, 600)" as any,
                    fontStyle: "var(--parkour-h5-style, italic)",
                    color: "var(--parkour-h5-colour, inherit)",
                    lineHeight: "var(--parkour-h5-leading, 1.5)" as any,
                    textTransform: "var(--parkour-h5-caps, none)" as any,
                    marginTop: "var(--parkour-h5-before, 10pt)",
                    marginBottom: "var(--parkour-h5-after, 3pt)",
                    borderBottom: "var(--parkour-h5-rule, none)",
                    paddingBottom: "var(--parkour-h5-rule-pad, 0)",
                },
                "& .ProseMirror h6": {
                    fontSize: "var(--parkour-h6-size, 9.5pt)",
                    fontWeight: "var(--parkour-h6-weight, 600)" as any,
                    fontStyle: "var(--parkour-h6-style, normal)",
                    color: "var(--parkour-h6-colour, inherit)",
                    lineHeight: "var(--parkour-h6-leading, 1.5)" as any,
                    // THE LETTER SPACING STAYS HARDCODED. It is not a property a style carries --
                    // it exists to keep the caps level legible -- and making it one would add a
                    // control nobody asked for to every style in the sheet.
                    textTransform: "var(--parkour-h6-caps, uppercase)" as any,
                    letterSpacing: ".04em",
                    marginTop: "var(--parkour-h6-before, 10pt)",
                    marginBottom: "var(--parkour-h6-after, 3pt)",
                    borderBottom: "var(--parkour-h6-rule, none)",
                    paddingBottom: "var(--parkour-h6-rule-pad, 0)",
                },
                "& .ProseMirror ul, & .ProseMirror ol": { margin: "0 0 10pt 0", paddingLeft: "0.3in" },
                "& .ProseMirror li > p": { margin: 0 },
                "& .ProseMirror blockquote": {
                    borderLeft: "3px solid #D8DDE4", margin: "0 0 10pt 0", paddingLeft: "12pt",
                    color: "#525968",
                },
                "& .ProseMirror hr": { border: "none", borderTop: "1px solid #D8DDE4", margin: "14pt 0" },
                "& .ProseMirror a": { color: "#1498D8" },

                // CHECKLISTS. A procedure's verification steps are written as one, and without the
                // list-style reset the box and the bullet both render.
                "& .ProseMirror ul[data-type='taskList']": { listStyle: "none", paddingLeft: "0.1in" },
                "& .ProseMirror ul[data-type='taskList'] li": {
                    display: "flex", alignItems: "flex-start", gap: "8pt",
                },
                "& .ProseMirror ul[data-type='taskList'] li > label": { marginTop: "2pt" },
                "& .ProseMirror ul[data-type='taskList'] li > div": { flexGrow: 1 },

                // TABLES WITH VISIBLE RULES. A controlled document's revision history, its approval
                // block and its specification limits are all tables, and a borderless one on screen
                // prints as a muddle of columns nobody can follow.
                "& .ProseMirror table": {
                    borderCollapse: "collapse", width: "100%", margin: "0 0 10pt 0",
                    tableLayout: "fixed",
                },
                // THE BORDER AND THE PADDING COME FROM THE TABLE, through custom properties it
                // sets on itself (TableOptions.ts) -- so Table options reaches every cell,
                // including cells added after the setting was made, with one declaration. The
                // fallbacks are the house defaults, which is what a table with no settings gets.
                "& .ProseMirror th, & .ProseMirror td": {
                    border: "var(--parkour-table-border-width, 0.75pt) solid "
                          + "var(--parkour-table-border-color, #9AA0A6)",
                    padding: "var(--parkour-table-cell-padding, 4pt) "
                           + "calc(var(--parkour-table-cell-padding, 4pt) * 1.5)",
                    verticalAlign: "top",
                },
                // A ZERO-WEIGHT BORDER IS NO BORDER, not a hairline: `0pt solid` still paints a
                // sub-pixel line in some browsers, and "None" has to mean none.
                "& .ProseMirror table[data-border-width='0'] th, & .ProseMirror table[data-border-width='0'] td": { border: "none" },
                "& .ProseMirror th": { backgroundColor: "#F1F3F4", fontWeight: 700, textAlign: "left" },
                // The cell the cursor is in, so a 3x3 table is navigable without guessing.
                "& .ProseMirror .selectedCell:after": {
                    content: '""', position: "absolute", inset: 0, backgroundColor: "rgba(20,152,216,.12)",
                    pointerEvents: "none",
                },
                "& .ProseMirror td, & .ProseMirror th": { position: "relative" },

                // THE BORDER UNDER THE POINTER. A column rule resizes, so it gets the resize
                // cursor; a row rule does not yet, so it gets the pointing hand -- it can be
                // picked and changed, which is what the cursor is promising.
                "& .ProseMirror.parkour-cursor-column, & .ProseMirror.parkour-cursor-column *": {
                    cursor: "col-resize",
                },
                "& .ProseMirror.parkour-cursor-row, & .ProseMirror.parkour-cursor-row *": {
                    cursor: "pointer",
                },

                // THE PICKED EDGE, drawn with an inset shadow rather than a border. With
                // border-collapse a cell's border is shared with its neighbour and the heavier one
                // wins, so painting the indicator as a border would sometimes be overdrawn by the
                // cell next door -- and would also change the table's geometry by a pixel at the
                // moment somebody is trying to see where a line is.
                "& .ProseMirror .parkour-border-picked": { position: "relative" },
                "& .ProseMirror .parkour-border-picked.parkour-border-top": {
                    boxShadow: "inset 0 2px 0 0 #1498D8",
                },
                "& .ProseMirror .parkour-border-picked.parkour-border-bottom": {
                    boxShadow: "inset 0 -2px 0 0 #1498D8",
                },
                "& .ProseMirror .parkour-border-picked.parkour-border-left": {
                    boxShadow: "inset 2px 0 0 0 #1498D8",
                },
                "& .ProseMirror .parkour-border-picked.parkour-border-right": {
                    boxShadow: "inset -2px 0 0 0 #1498D8",
                },

                // THE COLUMN RESIZE AFFORDANCE. The plugin puts `resize-cursor` on the editor while
                // the pointer is over a border and draws a handle at the edge being dragged; both
                // need styling here or the feature is invisible and feels broken.
                "& .ProseMirror.resize-cursor": { cursor: "col-resize" },
                "& .ProseMirror .column-resize-handle": {
                    position: "absolute", right: -2, top: 0, bottom: 0, width: 4,
                    backgroundColor: "#1498D8", pointerEvents: "none", zIndex: 20,
                },

                // A TAB IS A SPACER, and it has to be inline-block or a width means nothing on it.
                //
                // THE MINIMUM IS NOT ZERO. A tab that lands exactly on its stop would otherwise be
                // zero pixels wide and impossible to put a caret beside, so a tab with nowhere to
                // go still shows as a thin gap -- which is also what tells somebody it is there.
                "& .ProseMirror span[data-tab]": {
                    display: "inline-block",
                    minWidth: "2px",
                    verticalAlign: "baseline",
                    // Selectable as a unit, like the atom it is, and never a place text can break.
                    whiteSpace: "nowrap",
                },

                // SOMEBODY ELSE'S CURSOR.
                //
                // THE EXTENSION SHIPS MARKUP AND NO CSS, which is not a detail: unstyled, the label
                // is a <div> -- a BLOCK -- inside an inline span, sitting in the text flow. So the
                // other person's name was rendered as a line of its own, in the middle of the
                // paragraph, pushing the real text down. Each window showed the OTHER person's name
                // inserted into the prose, which made two identical documents look as though they
                // had diverged. They had not; the name was furniture being drawn as content.
                //
                // A CARET IS AN OVERLAY, NOT A CHARACTER. Absolute for the label, and negative
                // margins on the bar so its 2px takes no width -- otherwise every remote cursor
                // nudges the text sideways by two pixels and the two windows really do differ.
                //
                // THE COLOURS COME FROM THE EXTENSION, inline: border-color on the bar and
                // background-color on the label, one per peer. Nothing here sets a colour, so the
                // per-person identity it assigns survives.
                "& .ProseMirror .collaboration-carets__caret": {
                    position: "relative",
                    borderLeftWidth: "2px",
                    borderLeftStyle: "solid",
                    marginLeft: "-1px",
                    marginRight: "-1px",
                    wordBreak: "normal",
                    // NEVER IN THE WAY. A controlled document is edited by clicking into the text,
                    // and a cursor belonging to somebody else must not intercept that.
                    pointerEvents: "none",
                },
                // SHOWN WHEN THEY MOVE, THEN GONE. ProseMirror rebuilds this widget on every remote
                // selection change, so the animation restarts each time somebody types -- which is
                // what Google Docs does, and is the difference between knowing who is here and
                // reading their name over and over while trying to work.
                "& .ProseMirror .collaboration-carets__label": {
                    position: "absolute",
                    top: "-1.35em",
                    left: "-2px",
                    whiteSpace: "nowrap",
                    fontSize: 10.5,
                    lineHeight: 1.6,
                    fontWeight: 500,
                    letterSpacing: 0.2,
                    padding: "0 5px",
                    borderRadius: "3px 3px 3px 0",
                    color: "#FFFFFF",
                    userSelect: "none",
                    pointerEvents: "none",
                    opacity: 0,
                    animation: "parkour-caret-name 2.4s ease forwards",
                },
                "@keyframes parkour-caret-name": {
                    "0%": {opacity: 0},
                    "6%": {opacity: 1},
                    "62%": {opacity: 1},
                    "100%": {opacity: 0},
                },

                // FIGURES. Centred, never wider than the text column, and carrying their own
                // resize handle -- which is the only control an image needs and the one every word
                // processor puts in the same place.
                //
                // max-width 100% IS LOAD-BEARING. The stored width is in CSS pixels and a figure
                // inserted at its natural size can be wider than the page; without this the text
                // column scrolls sideways and the printed page clips. The renderer clamps the same
                // way, so screen and signed PDF agree.
                "& .ProseMirror .parkour-figure-wrap": {
                    position: "relative", display: "block", margin: "6px auto 8px",
                    width: "fit-content", maxWidth: "100%", lineHeight: 0,
                },
                "& .ProseMirror img.parkour-figure": {
                    display: "block", maxWidth: "100%", height: "auto", borderRadius: 2,
                },
                // `hidden` HAS TO WIN, and without this it does not. The browser hides a [hidden]
                // element with a UA rule of `display: none`, and ANY author rule setting display --
                // the flex and block below -- beats it. The placeholder stayed on screen above the
                // picture it was standing in for, which looks like the image failed to load while
                // it is sitting right there.
                "& .ProseMirror .parkour-figure-wrap [hidden]": { display: "none" },
                "& .ProseMirror .parkour-figure-pending": {
                    display: "flex", alignItems: "center", justifyContent: "center",
                    minWidth: 180, minHeight: 90, padding: "8px 16px",
                    border: "1px dashed #BDC1C6", borderRadius: 3, color: "#80868B",
                    font: "13px/1.4 Roboto, Arial, sans-serif", backgroundColor: "#FAFAFA",
                },
                // SELECTION IS AN OUTLINE, NOT A BACKGROUND. An atom node's default blue wash sits
                // ON TOP of the picture and hides exactly what you selected it to look at.
                "& .ProseMirror .parkour-figure-selected img.parkour-figure": {
                    outline: "2px solid #1498D8", outlineOffset: 1,
                },
                "& .ProseMirror .parkour-figure-handle": {
                    position: "absolute", right: -5, bottom: -5, width: 10, height: 10,
                    borderRadius: "2px", border: "1px solid #FFFFFF",
                    backgroundColor: "#1498D8", cursor: "nwse-resize",
                    opacity: 0, transition: "opacity .12s",
                },
                "& .ProseMirror .parkour-figure-wrap:hover .parkour-figure-handle": { opacity: 1 },
                "& .ProseMirror .parkour-figure-selected .parkour-figure-handle": { opacity: 1 },
                // THE GRIPPER. Zero-height anchor in the flow, handle hung outside the table's
                // top-left corner -- so it costs no layout and never moves the text.
                "& .ProseMirror .parkour-table-gripper-anchor": {
                    display: "block", position: "relative", height: 0,
                },
                "& .ProseMirror .parkour-table-gripper": {
                    position: "absolute", left: "-20px", top: "2px", width: 14, height: 14,
                    borderRadius: "3px", border: "1px solid #BDC1C6", backgroundColor: "#FFFFFF",
                    cursor: "grab", opacity: 0, transition: "opacity .12s",
                    // The four-way arrow, drawn rather than imported: one glyph, no font to wait for.
                    backgroundImage: "linear-gradient(#5F6368,#5F6368), linear-gradient(#5F6368,#5F6368)",
                    backgroundSize: "8px 1px, 1px 8px",
                    backgroundPosition: "center, center",
                    backgroundRepeat: "no-repeat",
                },
                // VISIBLE WHEN THE POINTER IS IN THE DOCUMENT, not only over the table itself: a
                // handle that appears only once you are already over the thing it belongs to is a
                // handle you have to know about to find.
                "& .ProseMirror:hover .parkour-table-gripper": { opacity: 0.45 },
                "& .ProseMirror .parkour-table-gripper:hover": { opacity: 1, borderColor: "#1498D8" },
                "& .ProseMirror .parkour-table-gripper:active": { cursor: "grabbing" },

                // A SELECTED TABLE HAS TO LOOK SELECTED, or clicking the gripper appears to do
                // nothing and the drag that follows is a guess.
                "& .ProseMirror table.ProseMirror-selectednode": {
                    outline: "2px solid #1498D8", outlineOffset: "2px",
                },

                // TRACKED CHANGES, drawn as every word processor has drawn them for thirty years:
                // an insertion underlined in its author's colour, a deletion struck through in it.
                // The colour comes from the mark itself (--author, set when it was made), so two
                // reviewers are two colours and stay that way wherever the document is opened.
                //
                // NOT background highlights. A procedure under review can be half suggestions, and
                // a page of coloured blocks is unreadable exactly when it most needs reading; an
                // underline leaves the words themselves untouched.
                // A BOOKMARKED PASSAGE, marked faintly: it is ordinary text that happens to
                // have a name, and the mark is for the author who needs to find it again rather
                // than for the reader. Nothing is printed -- a bookmark is a handle, not content.
                "& .ProseMirror .parkour-bookmark": {
                    borderBottom: "1px dashed #C4C7CE",
                },
                // A CROSS-REFERENCE reads as ordinary text and is not: it is computed, so it
                // carries a faint underline to say it is a reference rather than something typed.
                // Not a link colour -- in a printed procedure it is simply the section number, and
                // a blue "4.2" would be a promise of a hyperlink the paper cannot keep.
                "& .ProseMirror .parkour-xref": {
                    borderBottom: "1px dotted #A8A9AF",
                    cursor: "default",
                },
                // THE WORDS ARE GENERATED CONTENT, drawn from the decoration's attribute -- the
                // same way the heading numbers are. Nothing is written into the editor's DOM, so
                // ProseMirror's observer never sees a change it did not make.
                "& .ProseMirror .parkour-xref::after": {
                    content: "attr(data-text)",
                },
                // A REFERENCE WHOSE SECTION HAS GONE says so in the text rather than vanishing.
                "& .ProseMirror .parkour-xref-missing": {
                    color: "#B3261E",
                    borderBottom: "1px dotted #B3261E",
                },
                // A FOOTNOTE MARKER. Superscript, in the text colour -- not a link colour: on
                // paper it is a number, and blue would be a promise of a click the page cannot
                // keep. The number is generated content from the decoration's attribute, the same
                // way the heading numbers and the cross-references are drawn.
                // A FIGURE THE TEXT RUNS AROUND. `float` is the only thing in CSS that does
                // this, and it is the same mechanism the PDF uses one layer down -- ReportLab's
                // ImageAndFlowables lays the words beside the picture and then under it.
                //
                // THE MARGIN IS ON THE TEXT SIDE ONLY. A float with equal margins pulls away from
                // the page edge it is sitting against, which reads as a figure that is not quite
                // aligned with anything.
                // A CELL WHOSE TEXT BREAKS ITS COLUMN'S TYPE. Underlined rather than filled: a procedure is
    // printed and signed, and a coloured cell background would carry into the rendition as though
    // the author had meant it. This mark is a note to whoever is editing and must never be part of
    // the document -- which is also why it is a decoration rather than an attribute.
    "& .ProseMirror .parkour-cell-invalid": {
        textDecoration: "underline wavy #C0392B",
        textDecorationSkipInk: "none",
        textUnderlineOffset: "3px",
    },
    // A WORD THE ENTERPRISE GLOSSARY HAS A RELEASED DEFINITION FOR. Dotted and grey, deliberately
    // unlike the wavy red above it: that one says something is wrong, this one says something is
    // KNOWN, and a reader who cannot tell them apart will read every mark as a defect. Quiet
    // enough to leave on while writing -- a procedure can use a dozen approved terms in a
    // paragraph, and anything stronger would stripe the page.
    "& .ProseMirror .parkour-glossary-term": {
        borderBottom: "1px dotted #9AA0A6",
        cursor: "help",
    },
    "& .ProseMirror .parkour-figure-wrap[data-wrap='left']": {
                    float: "left", marginRight: "14px", marginBottom: "8px", marginTop: "2px",
                },
                "& .ProseMirror .parkour-figure-wrap[data-wrap='right']": {
                    float: "right", marginLeft: "14px", marginBottom: "8px", marginTop: "2px",
                },
                // AND A HEADING CLEARS IT. Text wrapping beside a picture is what somebody asked
                // for; a SECTION TITLE wrapping beside one is a procedure that has lost its
                // structure, and it is the one case where the float must stop.
                "& .ProseMirror h1, & .ProseMirror h2, & .ProseMirror h3, & .ProseMirror h4, & .ProseMirror h5, & .ProseMirror h6": {
                    clear: "both",
                },
                "& .ProseMirror .parkour-footnote": {
                    cursor: "pointer",
                    verticalAlign: "super",
                    fontSize: "0.72em",
                    lineHeight: 0,
                },
                "& .ProseMirror .parkour-footnote::after": {
                    content: "attr(data-text)",
                },
                // A NOTE NOBODY HAS WRITTEN YET. The marker is in the sentence and the foot of the
                // page is blank beside it; the author is the only person who can see that before
                // the PDF is made, so it is said on the marker rather than left to be noticed.
                "& .ProseMirror .parkour-footnote-empty::after": {
                    color: "#B3261E",
                },
                // THE BAND AT THE FOOT OF A SHEET. Absolutely positioned, so it takes no room in
                // the flow: the room is made by pagination stopping the text short on that sheet,
                // and this is painted into the gap. See pagination.ts.
                "& .ProseMirror .parkour-footnote-band": {
                    borderTop: "1px solid #C4C7CE",
                    paddingTop: "6px",
                    marginTop: "0",
                    // THE RULE IS SHORT, as it is in every printed book: a line across the whole
                    // measure reads as the end of the document rather than the start of its notes.
                    width: "33% !important",
                    userSelect: "none",
                },
                "& .ProseMirror .parkour-footnote-note": {
                    // THE NOTES ARE OUTSIDE THE MEASURE THE RULE DRAWS, so they run the full width
                    // of the text while the rule above them does not.
                    width: "300%",
                    fontSize: "9pt",
                    lineHeight: 1.45,
                    color: "#3C4043",
                    marginBottom: "2px",
                },
                "& .ProseMirror .parkour-footnote-note sup": {
                    marginRight: "3px",
                },
                "& .ProseMirror .parkour-footnote-note-empty": {
                    color: "#B3261E",
                    fontStyle: "italic",
                },
                "& .ProseMirror .parkour-toc": {
                    border: "1px solid #EDEFF3",
                    borderRadius: "2px",
                    padding: "12px 14px",
                    margin: "12px 0",
                },
                "& .ProseMirror .parkour-toc-entry": {
                    fontSize: "inherit",
                    lineHeight: 1.7,
                },
                "& .ProseMirror .parkour-toc-empty": {
                    color: "#A8A9AF",
                    fontStyle: "italic",
                },
                // A COMMENTED PHRASE, marked the way every editor marks one: a soft highlight
                // that says "there is a conversation here" without competing with the text. The
                // active thread is stronger. A RESOLVED THREAD KEEPS ITS ANCHOR AND LOSES THE
                // COLOUR -- the mark stays in the body so the thread still knows what it was
                // about, it just stops colouring the procedure.
                // THE HEADING NUMBER, drawn from the decoration rather than from the text --
                // see OutlineNumbers.ts. Nothing here is in the document, so it cannot be edited,
                // cannot be searched for, and cannot go stale when a section moves.
                "& .ProseMirror h1[data-number]::before, & .ProseMirror h2[data-number]::before, & .ProseMirror h3[data-number]::before, & .ProseMirror h4[data-number]::before": {
                    content: "attr(data-number)",
                    marginRight: "0.5em",
                },
                "& .ProseMirror .parkour-comment": {
                    backgroundColor: "#FFF3C4",
                    borderBottom: "1px solid #F5C518",
                    cursor: "pointer",
                },
                "& .ProseMirror .parkour-comment-active": {
                    backgroundColor: "#FFE8A3",
                },
                "& .ProseMirror .parkour-comment-resolved": {
                    backgroundColor: "transparent",
                    borderBottom: "none",
                },
                "& .ProseMirror ins.suggestion-insertion": {
                    textDecoration: "none",
                    borderBottom: "2px solid var(--author, #5ABCED)",
                    color: "var(--author, #5ABCED)",
                },
                "& .ProseMirror del.suggestion-deletion": {
                    textDecoration: "line-through",
                    textDecorationColor: "var(--author, #C0392B)",
                    textDecorationThickness: "2px",
                    color: "var(--author, #C0392B)",
                    opacity: 0.85,
                },

                // THE DOCUMENT'S OWN HEADER, when it has one: the mirror of the footer, ruled
                // underneath rather than above, and sitting above the first line of text.
                "& .ProseMirror .parkour-header": {
                    borderBottom: "1px solid #000000", paddingBottom: "4pt",
                    marginTop: "calc(var(--parkour-header-margin) - 1in)",
                    marginBottom: "calc(1in - var(--parkour-header-margin))",
                    fontSize: "8pt", color: "#202124",
                },
                "& .ProseMirror .parkour-header p": { margin: 0 },
                "& .ProseMirror .parkour-header table": { margin: 0, border: "none" },
                "& .ProseMirror .parkour-header td, & .ProseMirror .parkour-header th": {
                    border: "none", padding: 0,
                },

                // THE DOCUMENT'S OWN FOOTER, when it has one (PageElements). It is part of the
                // body, so it is styled here with everything else the page draws -- and it has to
                // come out looking like the automatic footer it replaces, because to the reader it
                // is the same footer with the author's words in it.
                "& .ProseMirror .parkour-footer": {
                    borderTop: "1px solid #000000", paddingTop: "4pt",
                    marginTop: "calc(1in - var(--parkour-footer-margin))",
                    marginBottom: "calc(var(--parkour-footer-margin) - 1in)",
                    fontSize: "8pt", color: "#202124",
                },
                "& .ProseMirror .parkour-footer p": { margin: 0 },
                // BORDERLESS, UNLIKE EVERY OTHER TABLE. The one-row table in a footer is a layout
                // for left/centre/right, not data -- ruled cells would print a box round the
                // document's identity on every page.
                "& .ProseMirror .parkour-footer table": { margin: 0, border: "none" },
                "& .ProseMirror .parkour-footer td, & .ProseMirror .parkour-footer th": {
                    border: "none", padding: 0,
                },

                // A FIELD LOOKS LIKE A FIELD WHILE YOU ARE WRITING, and like text when you print.
                // An author has to be able to see that "Rev. 1" is the record talking and not
                // something they can correct by typing over it; a reader of the printed page has
                // no use for the distinction and a grey box in a footer would just look broken.
                "& .ProseMirror .parkour-field": {
                    backgroundColor: "#F1F3F4", borderRadius: "3px", padding: "0 2px",
                    whiteSpace: "nowrap",
                },
                "@media print": {
                    "& .ProseMirror .parkour-field": { backgroundColor: "transparent", padding: 0 },
                    // A COMMENT HIGHLIGHT IS A CONVERSATION ABOUT THE PROCEDURE, not part of it,
                    // and a printed page is a controlled copy.
                    "& .ProseMirror .parkour-comment": {
                        backgroundColor: "transparent",
                        borderBottom: "none",
                    },
                    // A BOOKMARK IS A HANDLE, NOT CONTENT.
                    "& .ProseMirror .parkour-bookmark": { borderBottom: "none" },
                    // A CONTROL PRINTED INTO A SIGNED DOCUMENT is a mark nobody put there.
                    "& .ProseMirror .parkour-figure-handle": { display: "none" },
                    // Nor is somebody else's cursor. Whoever was in the document when it was
                    // printed is not part of what was printed.
                    "& .ProseMirror .collaboration-carets__caret": { display: "none" },
                },

                // A CLOSED REVISION STILL LOOKS LIKE A DOCUMENT. Greying the paper would say "this
                // is broken"; the toolbar being inert and t1's own sentence above it say "this is
                // released", which is the true thing.
                ...(editable ? {} : { "& .ProseMirror": { cursor: "default" } }),
            }}>
                {children}
                {/* INSIDE THE SHEET, so it prints with it and sits inside the margins -- and after
                    the content, so it follows the text down rather than floating. */}
                {footer}
            </Box>
        </Box>
    );
}
